/**
 * Permission registry.
 *
 * Convention: `{section}.{action}` keys, stored in `workspace_members.custom_permissions`.
 * Owner role bypasses every check. Admin role bypasses by default (custom_permissions
 * is honored if non-null on an admin, allowing "scoped admin" patterns).
 *
 * Action semantics:
 *   .view         — render the section in nav + access its routes
 *   .create       — add new records
 *   .edit.own     — edit records where created_by_user_id = auth.uid()
 *   .edit.any     — edit any record in the workspace (implies .edit.own)
 *   .delete.own   — delete own records
 *   .delete.any   — delete any record (implies .delete.own)
 *
 * `.any` implication of `.own` is enforced in `has_member_permission()` so the array
 * only stores the higher-grade key (no duplicate entries).
 */

export const PERMISSIONS = {
  // ── Properties
  'properties.view':       'View properties',
  'properties.create':     'Create properties',
  'properties.edit.own':   'Edit own properties',
  'properties.edit.any':   'Edit any property',
  'properties.delete.own': 'Delete own properties',
  'properties.delete.any': 'Delete any property',

  // ── Deals (disabled — nothing stored in deals yet)
  // 'deals.view':       'View deals',
  // 'deals.create':     'Create deals',
  // 'deals.edit.own':   'Edit own deals',
  // 'deals.edit.any':   'Edit any deal',
  // 'deals.delete.own': 'Delete own deals',
  // 'deals.delete.any': 'Delete any deal',

  // ── Pipeline (saved properties moving through deal stages)
  'pipeline.view':       'View pipeline',
  'pipeline.create':     'Save properties to pipeline',
  'pipeline.edit.own':   'Move/edit own pipeline items',
  'pipeline.edit.any':   'Move/edit any pipeline item',
  'pipeline.delete.own': 'Remove own pipeline items',
  'pipeline.delete.any': 'Remove any pipeline item',

  // ── Contacts (disabled — nothing stored in contacts yet)
  // 'contacts.view':       'View contacts',
  // 'contacts.create':     'Create contacts',
  // 'contacts.edit.own':   'Edit own contacts',
  // 'contacts.edit.any':   'Edit any contact',
  // 'contacts.delete.own': 'Delete own contacts',
  // 'contacts.delete.any': 'Delete any contact',

  // ── Tasks (disabled — no tasks saved yet)
  // 'tasks.view':       'View tasks',
  // 'tasks.create':     'Create tasks',
  // 'tasks.edit.own':   'Edit own tasks',
  // 'tasks.edit.any':   'Edit any task',
  // 'tasks.delete.own': 'Delete own tasks',
  // 'tasks.delete.any': 'Delete any task',

  // ── Notes (disabled — nothing stored in notes yet)
  // 'notes.view':       'View notes',
  // 'notes.create':     'Create notes',
  // 'notes.edit.own':   'Edit own notes',
  // 'notes.edit.any':   'Edit any note',
  // 'notes.delete.own': 'Delete own notes',
  // 'notes.delete.any': 'Delete any note',

  // ── Rehab
  'rehab.view':       'View rehab projects',
  'rehab.create':     'Create rehab projects',
  'rehab.edit.own':   'Edit own rehab projects',
  'rehab.edit.any':   'Edit any rehab project',
  'rehab.delete.own': 'Delete own rehab projects',
  'rehab.delete.any': 'Delete any rehab project',

  // ── Financing (permission keys remain `loans.*` — persisted + referenced by RLS)
  'loans.view':       'View financing',
  'loans.create':     'Create financing',
  'loans.edit.own':   'Edit own financing',
  'loans.edit.any':   'Edit any financing',
  'loans.delete.own': 'Delete own financing',
  'loans.delete.any': 'Delete any financing',

  // ── Rentals
  'rentals.view':       'View rentals',
  'rentals.create':     'Create rentals',
  'rentals.edit.own':   'Edit own rentals',
  'rentals.edit.any':   'Edit any rental',
  'rentals.delete.own': 'Delete own rentals',
  'rentals.delete.any': 'Delete any rental',

  // ── Property Management
  'property_mgmt.view':       'View property management',
  'property_mgmt.create':     'Create property-mgmt records',
  'property_mgmt.edit.own':   'Edit own property-mgmt records',
  'property_mgmt.edit.any':   'Edit any property-mgmt record',
  'property_mgmt.delete.own': 'Delete own property-mgmt records',
  'property_mgmt.delete.any': 'Delete any property-mgmt record',

  // ── Section 8
  'section8.view':       'View Section 8',
  'section8.create':     'Create Section 8 records',
  'section8.edit.own':   'Edit own Section 8 records',
  'section8.edit.any':   'Edit any Section 8 record',
  'section8.delete.own': 'Delete own Section 8 records',
  'section8.delete.any': 'Delete any Section 8 record',

  // ── Accounting
  'accounting.view':       'View accounting',
  'accounting.create':     'Add transactions',
  'accounting.edit.own':   'Edit own transactions',
  'accounting.edit.any':   'Edit any transaction',
  'accounting.delete.own': 'Delete own transactions',
  'accounting.delete.any': 'Delete any transaction',

  // ── Workspace administration (admin-territory, never grantable to plain members)
  'workspace.members.manage':  'Manage workspace members',
  'workspace.settings.manage': 'Manage workspace settings',
  'workspace.billing.manage':  'Manage billing',
} as const;

export type Permission = keyof typeof PERMISSIONS;

export const SECTIONS = [
  'properties',
  // 'deals',     // disabled — nothing stored in deals yet
  'pipeline',
  // 'contacts',  // disabled — nothing stored in contacts yet
  // 'tasks',     // disabled — no tasks saved yet
  // 'notes',     // disabled — nothing stored in notes yet
  'rehab',
  'loans',
  'rentals',
  'property_mgmt',
  'section8',
  'accounting',
] as const;

export type Section = (typeof SECTIONS)[number];

/** Default permissions for a freshly invited Team Member — view-only across the board. */
export const DEFAULT_MEMBER_PERMISSIONS: Permission[] = [
  'properties.view',
  // 'deals.view',     // disabled — nothing stored in deals yet
  'pipeline.view',
  // 'contacts.view',  // disabled — nothing stored in contacts yet
  // 'tasks.view',     // disabled — no tasks saved yet
  // 'notes.view',     // disabled — nothing stored in notes yet
  'rehab.view',
  'loans.view',
  'rentals.view',
  'property_mgmt.view',
  'section8.view',
  'accounting.view',
];

export interface PermissionGroup {
  label: string;
  section: Section;
  viewKey: Permission;
  /** Each entry is one row in the permissions dialog under this section. */
  writePermissions: { key: Permission; label: string }[];
}

const standardWritePermissions = (section: Section): { key: Permission; label: string }[] => [
  { key: `${section}.create` as Permission,     label: 'Create new records' },
  { key: `${section}.edit.own` as Permission,   label: 'Edit own records' },
  { key: `${section}.edit.any` as Permission,   label: 'Edit any record' },
  { key: `${section}.delete.own` as Permission, label: 'Delete own records' },
  { key: `${section}.delete.any` as Permission, label: 'Delete any record' },
];

export const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    label: 'Properties',
    section: 'properties',
    viewKey: 'properties.view',
    writePermissions: standardWritePermissions('properties'),
  },
  // Deals — disabled (nothing stored in deals yet)
  // {
  //   label: 'Deals',
  //   section: 'deals',
  //   viewKey: 'deals.view',
  //   writePermissions: standardWritePermissions('deals'),
  // },
  {
    label: 'Pipeline',
    section: 'pipeline',
    viewKey: 'pipeline.view',
    writePermissions: standardWritePermissions('pipeline'),
  },
  // Contacts — disabled (nothing stored in contacts yet)
  // {
  //   label: 'Contacts',
  //   section: 'contacts',
  //   viewKey: 'contacts.view',
  //   writePermissions: standardWritePermissions('contacts'),
  // },
  // Tasks — disabled (no tasks saved yet)
  // {
  //   label: 'Tasks',
  //   section: 'tasks',
  //   viewKey: 'tasks.view',
  //   writePermissions: standardWritePermissions('tasks'),
  // },
  // Notes — disabled (nothing stored in notes yet)
  // {
  //   label: 'Notes',
  //   section: 'notes',
  //   viewKey: 'notes.view',
  //   writePermissions: standardWritePermissions('notes'),
  // },
  {
    label: 'Rehab',
    section: 'rehab',
    viewKey: 'rehab.view',
    writePermissions: standardWritePermissions('rehab'),
  },
  {
    label: 'Financing',
    section: 'loans',
    viewKey: 'loans.view',
    writePermissions: standardWritePermissions('loans'),
  },
  {
    label: 'Rentals',
    section: 'rentals',
    viewKey: 'rentals.view',
    writePermissions: standardWritePermissions('rentals'),
  },
  {
    label: 'Property Management',
    section: 'property_mgmt',
    viewKey: 'property_mgmt.view',
    writePermissions: standardWritePermissions('property_mgmt'),
  },
  {
    label: 'Section 8',
    section: 'section8',
    viewKey: 'section8.view',
    writePermissions: standardWritePermissions('section8'),
  },
  {
    label: 'Accounting',
    section: 'accounting',
    viewKey: 'accounting.view',
    writePermissions: standardWritePermissions('accounting'),
  },
];

/**
 * Resolve whether a granted permission satisfies the requested permission,
 * applying the `.any` → `.own` implication rule. Used by the frontend
 * `canDo()` so a member with `deals.edit.any` doesn't need both keys stored.
 */
export function permissionsSatisfy(
  granted: readonly string[] | null | undefined,
  required: Permission,
): boolean {
  if (!granted || granted.length === 0) return false;
  if (granted.includes(required)) return true;
  if (required.endsWith('.edit.own')) {
    return granted.includes(required.replace('.edit.own', '.edit.any'));
  }
  if (required.endsWith('.delete.own')) {
    return granted.includes(required.replace('.delete.own', '.delete.any'));
  }
  return false;
}
